import { Modal } from 'ant-design-vue'
import type { Router } from 'vue-router'

export interface AliyunLiveExitGuardEntry {
  isBlocking: () => boolean,
  prepareForExit: () => Promise<void>
}

const entries = new Set<AliyunLiveExitGuardEntry>()
let warningVisible = false
let exitCheckPromise: Promise<boolean> | null = null
let installed = false

export function registerAliyunLiveExitGuard (entry: AliyunLiveExitGuardEntry) {
  entries.add(entry)
  return () => entries.delete(entry)
}

export function hasBlockingAliyunLiveExit () {
  return Array.from(entries).some(isEntryBlocking)
}

export function ensureAliyunLiveExitAllowed (): Promise<boolean> {
  if (exitCheckPromise) {
    return exitCheckPromise
  }
  const check = checkAliyunLiveExitAllowed()
  exitCheckPromise = check
  return check.finally(() => {
    if (exitCheckPromise === check) {
      exitCheckPromise = null
    }
  })
}

export function installAliyunLiveExitGuard (router: Router) {
  if (installed) {
    return
  }
  installed = true
  window.addEventListener('beforeunload', handleBeforeUnload)
  router.beforeEach(async () => {
    return await ensureAliyunLiveExitAllowed()
  })
}

async function checkAliyunLiveExitAllowed () {
  const snapshot = Array.from(entries)
  await Promise.all(snapshot.map(async entry => {
    try {
      await entry.prepareForExit()
    } catch (_error) {
      // Keep the last known state. An active known task remains blocking,
      // while a page without a known task is not locked by a network error.
    }
  }))
  if (!snapshot.some(entry => entries.has(entry) && isEntryBlocking(entry))) {
    return true
  }
  showExitWarning()
  return false
}

function isEntryBlocking (entry: AliyunLiveExitGuardEntry) {
  try {
    return entry.isBlocking()
  } catch (_error) {
    return false
  }
}

function showExitWarning () {
  if (warningVisible) {
    return
  }
  warningVisible = true
  Modal.warning({
    title: '请先停止推送阿里云直播',
    content: '当前页面正在推送阿里云直播，请先点击红色“停止推送阿里云直播”按钮，停止成功后再关闭页面。',
    okText: '我知道了',
    closable: false,
    keyboard: false,
    maskClosable: false,
    onOk: () => {
      warningVisible = false
    },
  })
}

function handleBeforeUnload (event: BeforeUnloadEvent) {
  if (!hasBlockingAliyunLiveExit()) {
    return
  }
  event.preventDefault()
  event.returnValue = ''
}
